import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import billingService from "../../services/billingService";
import {
  fetchBillsStart,
  fetchBillsSuccess,
  fetchBillsFailure,
  fetchBillByIdStart,
  fetchBillByIdSuccess,
  fetchBillByIdFailure,
  createBillStart,
  createBillSuccess,
  createBillFailure,
  updateBillStart,
  updateBillSuccess,
  updateBillFailure,
  deleteBillStart,
  deleteBillSuccess,
  deleteBillFailure,
  fetchPatientsStart,
  fetchPatientsSuccess,
  fetchPatientsFailure,
  createPatientStart,
  createPatientSuccess,
  createPatientFailure,
  updatePatientStart,
  updatePatientSuccess,
  updatePatientFailure,
  deletePatientStart,
  deletePatientSuccess,
  deletePatientFailure,
  fetchBillStatsStart,
  fetchBillStatsSuccess,
  fetchBillStatsFailure,
} from "../slices/billingSlice";
import {
  Bill,
  Patient,
  CreateBillRequest,
  UpdateBillRequest,
  CreatePatientRequest,
  BillSearchFilters,
} from "../../types/Billing";

// Bill Sagas
function* fetchBillsSaga(
  action: PayloadAction<BillSearchFilters | undefined>
): Generator<any, void, any> {
  try {
    console.log(
      "fetchBillsSaga: Action received with payload:",
      action.payload
    );
    const bills: Bill[] = yield call(billingService.getBills, action.payload);
    console.log("fetchBillsSaga: Bills received from service:", bills);
    yield put(fetchBillsSuccess(Array.isArray(bills) ? bills : []));
  } catch (error: any) {
    console.error("Fetch bills saga error:", error);
    yield put(fetchBillsSuccess([])); // Provide empty array on error
    yield put(
      fetchBillsFailure(
        error.response?.data?.message || "Failed to fetch bills"
      )
    );
  }
}

function* fetchBillByIdSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    const bill: Bill = yield call(billingService.getBillById, action.payload);
    yield put(fetchBillByIdSuccess(bill));
  } catch (error: any) {
    yield put(
      fetchBillByIdFailure(
        error.response?.data?.message || "Failed to fetch bill"
      )
    );
  }
}

function* createBillSaga(
  action: PayloadAction<CreateBillRequest>
): Generator<any, void, any> {
  try {
    const bill: Bill = yield call(billingService.createBill, action.payload);
    yield put(createBillSuccess(bill));
  } catch (error: any) {
    yield put(
      createBillFailure(
        error.response?.data?.message || "Failed to create bill"
      )
    );
  }
}

function* updateBillSaga(
  action: PayloadAction<{ id: string; billData: UpdateBillRequest }>
): Generator<any, void, any> {
  try {
    const bill: Bill = yield call(
      billingService.updateBill,
      action.payload.id,
      action.payload.billData
    );
    yield put(updateBillSuccess(bill));
  } catch (error: any) {
    yield put(
      updateBillFailure(
        error.response?.data?.message || "Failed to update bill"
      )
    );
  }
}

function* deleteBillSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    yield call(billingService.deleteBill, action.payload);
    yield put(deleteBillSuccess(action.payload));
  } catch (error: any) {
    yield put(
      deleteBillFailure(
        error.response?.data?.message || "Failed to delete bill"
      )
    );
  }
}

// Bill stats saga
function* fetchBillStatsSaga(): Generator<any, void, any> {
  try {
    console.log("billingSaga: fetchBillStatsSaga called");
    const response: any = yield call(billingService.getBillStats);
    console.log("billingSaga: fetchBillStatsSaga response:", response);
    // Handle the nested response structure from the backend
    const stats = response.data || response;
    yield put(fetchBillStatsSuccess(stats));
  } catch (error: any) {
    console.error("Fetch bill stats saga error:", error);
    // Provide default stats on error to prevent crashing
    const defaultStats = {
      totalBills: 0,
      totalRevenue: 0,
      pendingAmount: 0,
      pendingPayments: 0,
      totalPatients: 0,
      monthlyRevenue: 0,
      monthlyBills: 0,
      overdueAmount: 0,
    };
    yield put(fetchBillStatsSuccess(defaultStats));
    yield put(
      fetchBillStatsFailure(
        error.response?.data?.message || "Failed to fetch bill stats"
      )
    );
  }
}

// Patient Sagas
function* fetchPatientsSaga(): Generator<any, void, any> {
  try {
    console.log("fetchPatientsSaga: Starting to fetch patients");
    const patients: Patient[] = yield call(billingService.getPatients);
    console.log("fetchPatientsSaga: Received patients:", patients);
    console.log(
      "fetchPatientsSaga: Is patients array?",
      Array.isArray(patients)
    );
    yield put(fetchPatientsSuccess(Array.isArray(patients) ? patients : []));
  } catch (error: any) {
    console.error("Fetch patients saga error:", error);
    yield put(fetchPatientsSuccess([])); // Provide empty array on error
    yield put(
      fetchPatientsFailure(
        error.response?.data?.message || "Failed to fetch patients"
      )
    );
  }
}

function* createPatientSaga(
  action: PayloadAction<CreatePatientRequest>
): Generator<any, void, any> {
  try {
    const patient: Patient = yield call(
      billingService.createPatient,
      action.payload
    );
    yield put(createPatientSuccess(patient));
  } catch (error: any) {
    yield put(
      createPatientFailure(
        error.response?.data?.message || "Failed to create patient"
      )
    );
  }
}

function* updatePatientSaga(
  action: PayloadAction<{
    id: string;
    patientData: Partial<CreatePatientRequest>;
  }>
): Generator<any, void, any> {
  try {
    const patient: Patient = yield call(
      billingService.updatePatient,
      action.payload.id,
      action.payload.patientData
    );
    yield put(updatePatientSuccess(patient));
  } catch (error: any) {
    yield put(
      updatePatientFailure(
        error.response?.data?.message || "Failed to update patient"
      )
    );
  }
}

function* deletePatientSaga(
  action: PayloadAction<string>
): Generator<any, void, any> {
  try {
    yield call(billingService.deletePatient, action.payload);
    yield put(deletePatientSuccess(action.payload));
  } catch (error: any) {
    yield put(
      deletePatientFailure(
        error.response?.data?.message || "Failed to delete patient"
      )
    );
  }
}

export function* billingSaga(): Generator<any, void, any> {
  yield takeLatest(fetchBillsStart.type, fetchBillsSaga);
  yield takeLatest(fetchBillByIdStart.type, fetchBillByIdSaga);
  yield takeEvery(createBillStart.type, createBillSaga);
  yield takeEvery(updateBillStart.type, updateBillSaga);
  yield takeEvery(deleteBillStart.type, deleteBillSaga);
  yield takeLatest(fetchBillStatsStart.type, fetchBillStatsSaga);
  yield takeLatest(fetchPatientsStart.type, fetchPatientsSaga);
  yield takeEvery(createPatientStart.type, createPatientSaga);
  yield takeEvery(updatePatientStart.type, updatePatientSaga);
  yield takeEvery(deletePatientStart.type, deletePatientSaga);
}
